import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { operatorApi } from '../services/operatorApi'
import LoadingSpinner from './common/LoadingSpinner'
import ErrorState from './common/ErrorState'
import Pagination from './common/Pagination'
import ConfirmationModal from './common/ConfirmationModal'
import OperatorEditModal from '../pages/OperatorManagement/OperatorEditModal'
import OperatorCreateModal from '../pages/OperatorManagement/OperatorCreateModal'
import { PAGINATION_DEFAULTS } from '../config/api.config'
import { showError } from '../utils/toast'

function formatDate(dateString) {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(dateString) {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function StatusBadge({ isActive }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium text-xs ${
      isActive
        ? 'bg-green-100 text-green-700'
        : 'bg-gray-100 text-gray-500'
    }`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        isActive ? 'bg-green-400' : 'bg-gray-300'
      }`} />
      {isActive ? 'Active' : 'Suspended'}
    </span>
  )
}

export default function OperatorManagement() {
  const navigate = useNavigate()
  const location = useLocation()
  const createRouteOpen = location.pathname === '/operator/create'
  const [operators, setOperators] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(PAGINATION_DEFAULTS.PAGE)
  const [perPage, setPerPage] = useState(PAGINATION_DEFAULTS.PER_PAGE)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [operatorToDelete, setOperatorToDelete] = useState(null)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [operatorToToggle, setOperatorToToggle] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [operatorToEdit, setOperatorToEdit] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(createRouteOpen)

  useEffect(() => {
    loadOperators()
  }, [search, statusFilter, currentPage, perPage])

  async function loadOperators() {
    setLoading(true)
    setError(null)
    try {
      const params = { page: currentPage, per_page: perPage }
      if (search) params.search = search
      if (statusFilter !== 'all') params.status = statusFilter

      const data = await operatorApi.getOperators(params)
      setOperators(data.operators || [])
      const pagination = data.pagination || {}
      setTotalPages(pagination.last_page ?? data.total_pages ?? 1)
      setTotalItems(pagination.total ?? data.total ?? (data.operators || []).length)
      if (pagination.current_page && pagination.current_page !== currentPage) {
        setCurrentPage(pagination.current_page)
      }
    } catch (err) {
      console.error('Failed to load operators:', err)
      setError(err?.message ?? 'Failed to load operators')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (createRouteOpen) {
      setShowCreateModal(true)
    }
  }, [createRouteOpen])

  function handleCreate() {
    setShowCreateModal(true)
    if (!createRouteOpen) {
      navigate('/operator/create')
    }
  }

  function handleCreateClose() {
    setShowCreateModal(false)
    if (createRouteOpen) {
      navigate('/operator?view=operators', { replace: true })
    }
  }

  function handleEdit(operator) {
    setOperatorToEdit(operator)
    setShowEditModal(true)
  }

  function handleEditClose() {
    setShowEditModal(false)
    setOperatorToEdit(null)
  }

  function handleToggleClick(operator) {
    setOperatorToToggle(operator)
    setShowStatusModal(true)
  }

  async function confirmToggleStatus() {
    if (!operatorToToggle) return
    const operator = operatorToToggle
    setActionLoading(true)
    try {
      await operatorApi.toggleOperatorStatus(operator.id)
      setShowStatusModal(false)
      setOperatorToToggle(null)
      await loadOperators()
    } catch (err) {
      console.error('Failed to toggle status:', err)
    } finally {
      setActionLoading(false)
    }
  }

  async function confirmDelete() {
    if (!operatorToDelete) return
    const id = operatorToDelete
    setActionLoading(true)
    try {
      await operatorApi.deleteOperator(id)
      setShowDeleteModal(false)
      setOperatorToDelete(null)
      await loadOperators()
    } catch (err) {
      console.error('Failed to delete operator:', err)
    } finally {
      setActionLoading(false)
    }
  }

  function handleDeleteClick(operator) {
    setOperatorToDelete(operator.id)
    setShowDeleteModal(true)
  }

  const filteredOperators = operators.filter((op) => {
    if (statusFilter === 'active' && !op.is_active) return false
    if (statusFilter === 'inactive' && op.is_active) return false
    return true
  })

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Operator Management</h2>
          <p className="text-xs text-gray-400 mt-0.5">Manage BillJean team members</p>
        </div>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Operator
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              setStatusFilter('all')
              setCurrentPage(1)
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setStatusFilter('active')
              setCurrentPage(1)
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'active'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => {
              setStatusFilter('inactive')
              setCurrentPage(1)
            }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'inactive'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            Suspended
          </button>
        </div>
        <div className="relative">
          <svg className="absolute left-2.5 top-2 text-gray-400 pointer-events-none" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search operators..."
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              setCurrentPage(1)
            }}
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-64"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner fullScreen text="Loading operators..." />
      ) : error ? (
        <ErrorState message={error} onRetry={loadOperators} />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Operator</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Login</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredOperators.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-sm text-gray-400">
                    No operators found
                  </td>
                </tr>
              ) : (
                filteredOperators.map(operator => (
                  <tr key={operator.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                          {operator.name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{operator.name}</div>
                          <div className="text-xs text-gray-400">{operator.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge isActive={operator.is_active} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDateTime(operator.last_login_at)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(operator.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleClick(operator)}
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            operator.is_active
                              ? 'text-orange-600 hover:bg-orange-50'
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={operator.is_active ? 'Suspend' : 'Activate'}
                        >
                          {operator.is_active ? 'Suspend' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleEdit(operator)}
                          className="px-2 py-1 text-indigo-600 hover:bg-indigo-50 rounded text-xs font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteClick(operator)}
                          className="px-2 py-1 text-red-600 hover:bg-red-50 rounded text-xs font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1) }}
          />
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { if (!actionLoading) { setShowDeleteModal(false); setOperatorToDelete(null) } }}
        onConfirm={confirmDelete}
        title="Delete operator"
        message="Are you sure you want to delete this operator? This action cannot be undone."
        confirmText={actionLoading ? 'Deleting...' : 'Delete'}
        type="danger"
      />

      <ConfirmationModal
        isOpen={showStatusModal}
        onClose={() => { if (!actionLoading) { setShowStatusModal(false); setOperatorToToggle(null) } }}
        onConfirm={confirmToggleStatus}
        title={operatorToToggle?.is_active ? 'Suspend operator' : 'Activate operator'}
        message={
          operatorToToggle?.is_active
            ? `Suspend ${operatorToToggle?.name || 'this operator'}? They will no longer be able to sign in.`
            : `Activate ${operatorToToggle?.name || 'this operator'}? They will be able to sign in again.`
        }
        confirmText={actionLoading ? 'Updating...' : (operatorToToggle?.is_active ? 'Suspend' : 'Activate')}
        type={operatorToToggle?.is_active ? 'warning' : 'info'}
      />

      <OperatorEditModal
        isOpen={showEditModal}
        operator={operatorToEdit}
        onClose={handleEditClose}
        onSuccess={loadOperators}
      />

      <OperatorCreateModal
        isOpen={showCreateModal}
        onClose={handleCreateClose}
        onSuccess={loadOperators}
      />
    </div>
  )
}
